// Moving a favorite or a folder in a place's list (drag and Alt+↑/↓).
//
// The root is one order of items and folders, so an item can also sit between
// two folders. Items move between the root and the folders; folders move in
// the root only (dropped on a folder they do not go in: no nesting).
//
// A move names the entry it goes before (or the end), not an index: the list
// on the screen leaves out kinds this app does not show, and those keep their
// place among the rest.
import { isOptimisticId } from "../data/optimistic";
import type { FavoriteFolder, FavoriteItem, FavoriteList } from "./favorites";

export interface EntryRef {
  type: "item" | "folder";
  id: string;
}

export interface FavoriteMove {
  entry: EntryRef;
  /** The folder it goes into; null = the root. A folder always goes to the root. */
  folderId: string | null;
  /** The entry it goes before in there; null = the end. */
  before: EntryRef | null;
}

/** Where a drop would land: a line between rows, or onto a folder (its end). */
export type FavoriteDrop = FavoriteMove & { into: boolean };

const same = (a: EntryRef | null, b: EntryRef | null) =>
  a !== null && b !== null && a.type === b.type && a.id === b.id;

const refOf = (e: FavoriteItem | FavoriteFolder): EntryRef => ({
  type: e.type,
  id: e.id,
});

/** The entries of the root (null) or of a folder. */
function entriesOf(
  list: FavoriteList,
  folderId: string | null,
): Array<FavoriteItem | FavoriteFolder> {
  if (folderId === null) return list.root;
  const folder = list.root.find(
    (e): e is FavoriteFolder => e.type === "folder" && e.id === folderId,
  );
  return folder?.items ?? [];
}

/** The folder an item is in (null = the root), or undefined if it is not there. */
export function containerOf(
  list: FavoriteList,
  entry: EntryRef,
): string | null | undefined {
  if (list.root.some((e) => same(refOf(e), entry))) return null;
  if (entry.type === "folder") return undefined;
  for (const e of list.root)
    if (e.type === "folder" && e.items.some((i) => i.id === entry.id))
      return e.id;
  return undefined;
}

/** The list after the move (the same list when it cannot be made). */
export function moveFavorite(
  list: FavoriteList,
  move: FavoriteMove,
): FavoriteList {
  const { entry, before } = move;
  const folderId = entry.type === "folder" ? null : move.folderId;
  if (containerOf(list, entry) === undefined || same(entry, before))
    return list;
  if (
    folderId !== null &&
    !list.root.some((e) => e.type === "folder" && e.id === folderId)
  )
    return list;
  let moving: FavoriteItem | FavoriteFolder | undefined;
  const take = <T extends FavoriteItem | FavoriteFolder>(xs: T[]): T[] =>
    xs.filter((x) => {
      if (!same(refOf(x), entry)) return true;
      moving = x;
      return false;
    });
  const root = take(list.root).map((e) =>
    e.type === "folder" ? { ...e, items: take(e.items) } : e,
  );
  if (!moving) return list;
  const placed: FavoriteItem | FavoriteFolder =
    moving.type === "item" ? { ...moving, folderId } : moving;
  const insert = <T extends FavoriteItem | FavoriteFolder>(xs: T[], x: T) => {
    const at = before ? xs.findIndex((y) => same(refOf(y), before)) : -1;
    return at < 0 ? [...xs, x] : [...xs.slice(0, at), x, ...xs.slice(at)];
  };
  if (folderId === null) return { ...list, root: insert(root, placed) };
  return {
    ...list,
    root: root.map((e) =>
      e.type === "folder" && e.id === folderId
        ? { ...e, items: insert(e.items, placed as FavoriteItem) }
        : e,
    ),
  };
}

/** Whether the move leaves everything where it is (no line is shown for it). */
export function isNoopMove(list: FavoriteList, move: FavoriteMove): boolean {
  const from = containerOf(list, move.entry);
  const to = move.entry.type === "folder" ? null : move.folderId;
  if (from === undefined || from !== to) return from === undefined;
  if (same(move.entry, move.before)) return true;
  const xs = entriesOf(list, from);
  const at = xs.findIndex((x) => same(refOf(x), move.entry));
  const next = xs[at + 1];
  return move.before === null
    ? next === undefined
    : same(next ? refOf(next) : null, move.before);
}

/**
 * The body of PATCH /api/me/favorites/order for the list after a move: the
 * order of the one container it went into. The server puts the entries it
 * was not given after the listed ones, and an item listed in the root leaves
 * its folder. Rows still being made have no id yet and are left out.
 */
export function reorderBody(
  next: FavoriteList,
  move: FavoriteMove,
): {
  place: string;
  root?: EntryRef[];
  items?: { id: string; folderId: string }[];
} {
  const folderId = move.entry.type === "folder" ? null : move.folderId;
  const kept = (e: { id: string }) => !isOptimisticId(e.id);
  if (folderId === null)
    return { place: next.place, root: next.root.filter(kept).map(refOf) };
  return {
    place: next.place,
    items: entriesOf(next, folderId)
      .filter(kept)
      .map((i) => ({ id: i.id, folderId })),
  };
}

/** Alt+↑ / Alt+↓: one step up or down inside the same container. */
export function stepMove(
  list: FavoriteList,
  entry: EntryRef,
  up: boolean,
): FavoriteMove | null {
  const folderId = containerOf(list, entry);
  if (folderId === undefined) return null;
  const xs = entriesOf(list, folderId);
  const at = xs.findIndex((x) => same(refOf(x), entry));
  if (up) {
    if (at <= 0) return null;
    return { entry, folderId, before: refOf(xs[at - 1]) };
  }
  if (at >= xs.length - 1) return null;
  const after = xs[at + 2];
  return { entry, folderId, before: after ? refOf(after) : null };
}

/** A row of the list as the drop reads it (from its data attributes). */
export interface DropRow {
  entry: EntryRef;
  /** The folder the row is in; null = the root (a folder's own row too). */
  folderId: string | null;
  top: number;
  bottom: number;
}

/**
 * Where a drop at `y` lands, reading the rows from the top.
 * - Holding an item: on an item, the upper half is before it and the lower
 *   half after it (in its container); on a folder's row, the upper half is
 *   before the folder in the root and the lower half into the folder.
 * - Holding a folder: only the root's rows count, the upper half is before it.
 * - Under every row (a gap, below the list): the end of the root.
 * A folder passed over is not dragged along: inside a folder is only its own
 * row's lower half and its items, so there is always a way back to the root.
 */
export function dropAt(
  rows: readonly DropRow[],
  holding: EntryRef,
  y: number,
): FavoriteDrop {
  const end: FavoriteDrop = {
    entry: holding,
    folderId: null,
    before: null,
    into: false,
  };
  const candidates =
    holding.type === "folder" ? rows.filter((r) => r.folderId === null) : rows;
  for (const [i, row] of candidates.entries()) {
    const mid = (row.top + row.bottom) / 2;
    if (holding.type === "folder" || row.entry.type === "folder") {
      if (y < mid)
        return {
          entry: holding,
          folderId: null,
          before: row.entry,
          into: false,
        };
      if (holding.type === "item" && y < row.bottom)
        return {
          entry: holding,
          folderId: row.entry.id,
          before: null,
          into: true,
        };
      continue;
    }
    if (y >= row.bottom) continue;
    if (y < mid)
      return {
        entry: holding,
        folderId: row.folderId,
        before: row.entry,
        into: false,
      };
    // After it: before the next row of the same container, or its end.
    const next = candidates
      .slice(i + 1)
      .find((r) =>
        row.folderId === null
          ? r.folderId === null
          : r.folderId === row.folderId,
      );
    return {
      entry: holding,
      folderId: row.folderId,
      before: next ? next.entry : null,
      into: false,
    };
  }
  return end;
}
